import React, { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import queryString from 'query-string'
import { Timestamp } from 'google-protobuf/google/protobuf/timestamp_pb'
import { Dropdown } from 'react-bootstrap'
import { DATE_FORMATS } from 'src/Utils/dateConstants'
import { Range } from 'react-date-range'
import { DateRangeModal } from 'src/components/Reusable/TrusteroDateRange/TrusteroDateRange'
import { useNavigateContext } from 'src/components/Reusable/Navigation/NavigateContext'
import { formatTimestamp } from 'src/Utils/formatDate'
import { CalendarSVG } from 'src/components/Icons/Basic'
import { FlexRow, FlexAlign } from 'src/components/Reusable/Flex'
import { FilterParam } from '../../../FilterBar.types'
import { SimpleDropdownItems } from '../../FilterDropdown'
import { DropdownItemType } from '../../FilterDropdown.constants'
import { DropDownMenuWithNesting } from '../../FilterDropdown.styles'
import {
  FilterCountChip,
  FilterDropdownToggle,
} from '../../../FilterBar.styles'
import { DashboardDateRangeDefaults } from './DashboardDateRangeFilterDropdown.types'
import { getDashboardDateRangeToggleText } from './DashboardDateRangeFilterDropdown.helpers'

export const DashboardDateRangeFilterDropdown = React.memo((): JSX.Element => {
  const location = useLocation()
  const navigate = useNavigate()
  const [showDatePicker, setShowDatePicker] = useState(false)
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [range, setRange] = useState({
    startDate: undefined,
    endDate: undefined,
  } as Range)
  const searchParamsFallback = useMemo(() => {
    return queryString.parse(location.search, {
      arrayFormat: 'bracket',
    })
  }, [location])
  const {
    navigateState: { parsedQueryParams },
  } = useNavigateContext()
  const searchParams = parsedQueryParams || searchParamsFallback
  const defaultSet = useMemo(
    () => new Set(Array.from(Object.values(DashboardDateRangeDefaults))),
    [],
  )

  const appliedFilters: Record<string, string[]> = useMemo(() => {
    const applied: Record<string, string[]> = {}
    const preprocessed = searchParams[FilterParam.DASHBOARD_DATE_RANGE]
    if (!preprocessed) return {}
    if (Array.isArray(preprocessed)) {
      if (defaultSet.has(preprocessed[0] as DashboardDateRangeDefaults)) {
        applied[FilterParam.DASHBOARD_DATE_RANGE] = preprocessed
      } else {
        applied[FilterParam.DASHBOARD_DATE_RANGE] = preprocessed[0].split('-')
      }
    } else {
      if (defaultSet.has(preprocessed as DashboardDateRangeDefaults)) {
        applied[FilterParam.DASHBOARD_DATE_RANGE] = [preprocessed]
      } else {
        applied[FilterParam.DASHBOARD_DATE_RANGE] = preprocessed
          ? preprocessed.split('-')
          : []
      }
    }
    return applied
  }, [searchParams, defaultSet])

  const appliedDateRange = useMemo(() => {
    if (
      !appliedFilters[FilterParam.DASHBOARD_DATE_RANGE]?.length ||
      defaultSet.has(
        appliedFilters[
          FilterParam.DASHBOARD_DATE_RANGE
        ][0] as DashboardDateRangeDefaults,
      )
    )
      return { startDate: undefined, endDate: undefined }
    return {
      startDate: new Date(
        parseInt(appliedFilters[FilterParam.DASHBOARD_DATE_RANGE][0], 10),
      ),
      endDate: new Date(
        parseInt(appliedFilters[FilterParam.DASHBOARD_DATE_RANGE][1], 10),
      ),
    } as Range
  }, [appliedFilters, defaultSet])

  useEffect(() => {
    if (!range.startDate || !range.endDate) return
    // make sure to use UTC time
    const start = new Date(range.startDate + 'Z')
    const end = new Date(range.endDate + 'Z')
    const updatedHref = queryString.stringifyUrl(
      {
        url: location.pathname,
        query: {
          ...searchParams,
          [FilterParam.DASHBOARD_DATE_RANGE]: [
            `${start.getTime()}-${end.getTime()}`,
          ],
        },
      },
      { arrayFormat: 'bracket' },
    )
    const currentHref = queryString.stringifyUrl(
      {
        url: location.pathname,
        query: {
          ...searchParams,
        },
      },
      { arrayFormat: 'bracket' },
    )
    if (currentHref !== updatedHref) {
      navigate(updatedHref, { replace: true })
      setRange({ startDate: undefined, endDate: undefined })
      setDropdownOpen(false)
    }
  }, [range, searchParams, location.pathname, navigate, appliedDateRange])

  // Close dropdown when location/searchParams change (after navigation)
  useEffect(() => {
    setDropdownOpen(false)
  }, [location.search])

  const filterValues: DropdownItemType[] = useMemo(
    () => [
      {
        value: DashboardDateRangeDefaults.LAST_7_DAYS,
        label: 'Last 7 Days',
      },
      {
        value: DashboardDateRangeDefaults.LAST_30_DAYS,
        label: 'Last 30 Days',
      },
      {
        value: DashboardDateRangeDefaults.LAST_90_DAYS,
        label: 'Last 90 Days',
      },
      {
        value: DashboardDateRangeDefaults.LAST_YEAR,
        label: 'Last Year',
      },
      {
        value: 'custom',
        label: 'Custom',
        customOnClick: () => setShowDatePicker(true),
      },
    ],
    [],
  )

  return (
    <Dropdown show={dropdownOpen} onToggle={setDropdownOpen}>
      <Dropdown.Toggle as={FilterDropdownToggle} id="date-range-filter">
        <FlexRow align={FlexAlign.CENTER} gap={10}>
          <CalendarSVG />
          {getDashboardDateRangeToggleText(appliedDateRange, appliedFilters)}
          {appliedDateRange.startDate && appliedDateRange.endDate && (
            <FilterCountChip ml="xxs">
              {`${formatTimestamp(
                Timestamp.fromDate(appliedDateRange.startDate),
                DATE_FORMATS.ISO,
              )} to ${formatTimestamp(
                Timestamp.fromDate(appliedDateRange.endDate),
                DATE_FORMATS.ISO,
              )}`}
            </FilterCountChip>
          )}
        </FlexRow>
      </Dropdown.Toggle>
      {showDatePicker && (
        <DateRangeModal
          range={appliedDateRange}
          rangeSet={setRange}
          editingSet={setShowDatePicker}
          enforcedOffsetDays={7}
        />
      )}
      <DropDownMenuWithNesting>
        <SimpleDropdownItems
          filterParam={FilterParam.DASHBOARD_DATE_RANGE}
          filterValues={filterValues}
          location={location}
          searchParams={searchParams}
          appliedFilters={appliedFilters}
          replaceParam
        />
      </DropDownMenuWithNesting>
    </Dropdown>
  )
})
